import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * usePopoverPosition + PopoverPortal — shared infrastructure for the
 * InlineEdit popover-using primitives (Dropdown, MultiSelect, DateTime,
 * Assignee).
 *
 * Two problems being solved:
 *  1. Popovers rendered as descendants of a card with `overflow:auto`
 *     get clipped or scroll out of view. Portaling to document.body
 *     escapes any overflow ancestor.
 *  2. When the anchor's height changes mid-render (e.g. multi-select
 *     chips wrapping to a new line, or assignee picker switching to a
 *     selected state), the popover position becomes stale. ResizeObserver
 *     on the anchor recomputes on resize.
 *
 * Positioning uses viewport coordinates (position:fixed). Anchored under
 * the trigger with a 4px gap. The popover flips up when there isn't
 * enough room below.
 */

export interface PopoverCoords {
  top: number;
  left: number;
}

export function usePopoverPosition(
  anchorRef: React.RefObject<HTMLElement>,
  open: boolean,
  estimatedHeight = 280,
  // When > 0, clamp the popover's left so a wide popover anchored near the
  // right edge of the viewport doesn't bleed off-screen. Default 0 keeps the
  // historical behavior (left = anchor.left, no horizontal clamp) for the
  // many narrow InlineEdit popovers that already fit. The 900px Todos filter
  // popover passes its width so it slides left to stay on-screen — fixes the
  // dashboard-widget filter bleed reported 2026-05-29.
  estimatedWidth = 0,
): PopoverCoords {
  const [coords, setCoords] = useState<PopoverCoords>({ top: 0, left: 0 });

  const recompute = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    const spaceAbove = r.top;
    // If there isn't room for ~estimatedHeight below and there's more room
    // above, flip the popover up. Otherwise anchor under the trigger.
    const top =
      spaceBelow < estimatedHeight && spaceAbove > spaceBelow
        ? r.top - estimatedHeight - 4
        : r.bottom + 4;
    let left = r.left;
    if (estimatedWidth > 0) {
      const MARGIN = 12;
      // Effective width can't exceed the viewport (popovers use
      // maxWidth: calc(100vw - 48px)); clamp against the smaller of the two.
      const effectiveWidth = Math.min(estimatedWidth, window.innerWidth - 2 * MARGIN);
      const maxLeft = window.innerWidth - effectiveWidth - MARGIN;
      left = Math.max(MARGIN, Math.min(left, maxLeft));
    }
    setCoords({ top, left });
  }, [anchorRef, estimatedHeight, estimatedWidth]);

  // Recompute when opening + on any anchor resize / window resize / scroll.
  useLayoutEffect(() => {
    if (!open) return;
    recompute();

    const ro = new ResizeObserver(() => recompute());
    if (anchorRef.current) ro.observe(anchorRef.current);

    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, recompute, anchorRef]);

  return coords;
}

interface PopoverPortalProps {
  open: boolean;
  coords: PopoverCoords;
  onClickOutside?: (e: MouseEvent) => void;
  /** Refs that should NOT count as outside-clicks. The portal already
   *  treats clicks inside itself as inside; this is for the anchor (so
   *  clicking the trigger again doesn't fire outside). */
  ignoreRefs?: Array<React.RefObject<HTMLElement>>;
  /** Opt-in 120ms ease-out open motion (fade + 4px rise), behind
   *  prefers-reduced-motion. Default off so existing InlineEdit popovers
   *  keep their instant mount; HubSelect/HubDateField opt in (CD-06). */
  animate?: boolean;
  children: React.ReactNode;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Renders children into a document.body portal at the given viewport
 * coordinates with `position: fixed`. Click-outside calls onClickOutside.
 * Z-index is 2000 — high enough to clear the modal's 1300-ish backdrop.
 */
export const PopoverPortal: React.FC<PopoverPortalProps> = ({
  open,
  coords,
  onClickOutside,
  ignoreRefs,
  animate,
  children,
}) => {
  const portalRef = useRef<HTMLDivElement>(null);
  // Opt-in enter transition: mount at opacity 0 / +4px, settle on the next
  // frame. Skipped entirely under prefers-reduced-motion.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!animate || !open || prefersReducedMotion()) {
      setEntered(true);
      return;
    }
    setEntered(false);
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [animate, open]);

  useEffect(() => {
    if (!open || !onClickOutside) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (portalRef.current?.contains(t)) return;
      for (const r of ignoreRefs ?? []) {
        if (r.current?.contains(t)) return;
      }
      onClickOutside(e);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClickOutside, ignoreRefs]);

  if (!open) return null;

  return createPortal(
    <div
      ref={portalRef}
      style={{
        position: 'fixed',
        top: coords.top,
        left: coords.left,
        zIndex: 2000,
        ...(animate
          ? {
              opacity: entered ? 1 : 0,
              transform: entered ? 'translateY(0)' : 'translateY(4px)',
              transition:
                'opacity 120ms cubic-bezier(.16,1,.3,1), transform 120ms cubic-bezier(.16,1,.3,1)',
            }
          : null),
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
};
